/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
/** @jsxFrag Fragment */
import { jsx, css, Global } from "@emotion/core"
import { ConfigProvider } from "@duxton-web/ui"
import { hot } from "react-hot-loader"
import { ThemeProvider } from "emotion-theming"
import { RootRouter } from "router/router"
import { duxtonTheme, theme } from "styles/theme"
import { Provider } from "react-redux"
import { store } from "store/store"
import { globalStyle, commonStyle } from "styles"
import { GlobalLoadingContainer } from "containers/GlobalLoadingContainer"
import { QueryClientProvider, QueryClient } from "@tanstack/react-query"
import { Fragment, lazy } from "react"
import LazyLoadWrapper from "components/LazyLoadWrapper"
import { isPrd } from "constants/domain"
import { MART_ECOMM_MIGRATION_PREVIEW } from "constants/routes"

import "normalize.css"
import "grab-ui/dist/theme.less"
import "styles/duxton-global.less"
import "antd/lib/drawer/style/index.css" // still need this for MenuGroupV2 drawers
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 5 * 60 * 1000, // 5 minutes - how long data is considered fresh
    },
  },
})

dayjs.extend(utc)
dayjs.extend(timezone)

// Rendered only outside production, and split out of the eager bundle so the
// ~545KB of dev-tools source is never downloaded or parsed by a merchant.
const AiDevTools = lazy(async () => ({
  default: (await import("@grab-food/web-ai-dev-tools")).AiDevTools,
}))

// React Router's `<Route exact>` matches the trailing-slash form too, so
// normalize before comparing or the tool would still mount there.
const isMigrationPreviewPath = pathname =>
  pathname.replace(/\/+$/, "") === MART_ECOMM_MIGRATION_PREVIEW

window.ADS = true
window.MEX_BILLING = true
const App = () => (
  <Provider store={store}>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <ConfigProvider theme={duxtonTheme}>
          <Global
            styles={css`
              ${globalStyle};
              ${commonStyle};
            `}
          />
          <div
            css={css`
              height: 100vh;
            `}
          >
            <RootRouter />
            <GlobalLoadingContainer />
            {/*
              `LazyLoadWrapper` builds its fallback as `fallback || <Spin/>`,
              so `null` would select the default spinner. This overlay is
              invisible until clicked — an empty fragment is what "no fallback"
              has to look like here. The `@jsxFrag` pragma above is what makes
              `<></>` compile against the imported `Fragment`: without it the
              `@jsx jsx` pragma emits `React.Fragment`, and this file has no
              `React` binding, so the fallback throws on first render.
            */}
            {!isPrd() && !isMigrationPreviewPath(window.location.pathname) && (
              <LazyLoadWrapper fallback={<></>}>
                <AiDevTools
                  repo="food-merchant-portal"
                  isProduction={isPrd()}
                  tinker={{ jiraLabel: "dobby_local_food_merchant_portal" }}
                />
              </LazyLoadWrapper>
            )}
          </div>
        </ConfigProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </Provider>
)

export default hot(module)(App)
